import React, { useState, useEffect, useContext } from "react";
import { Home, BookOpen, FileText, Calendar, Settings, User, Hand, X, Archive } from 'lucide-react';
import { NavLink, useLocation } from "react-router-dom";
import { ApiUrlContext } from "../App";

const menu = [
  { label: "Anasayfa", path: "/panel", icon: <Home /> },
  { label: "Kurslar", path: "/courses", icon: <BookOpen /> },
  { label: "Ödevler", path: "/assessments", icon: <FileText /> },
  { label: "Randevu Sistemi", path: "/appointments", icon: <Calendar /> },
  { label: "Malzeme Ödünç Talebi", path: "/borrow-requests", icon: <Archive /> },
  { label: "Admin Panel", path: "/admin", icon: <Settings />, adminOnly: true },
  { label: "Öğretmen Panel", path: "/instructor", icon: <User />, instructorOnly: true },
  { label: "Bize Katıl", path: "/contact", icon: <Hand /> }, // Yeni sayfa eklendi
];

// Minimalist ve Modern CSS Stilleri
const styles = `
.sidebar {
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, sans-serif;
  background: linear-gradient(180deg, #ffffff 0%, #fafbfc 100%);
  border-right: 1px solid #edf2f7;
  backdrop-filter: blur(10px);
}

.sidebar-nav .nav-link {
  display: flex;
  align-items: center;
  padding: 14px 24px;
  text-decoration: none;
  color: #4a5568;
  margin: 2px 16px;
  border-radius: 12px;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  font-size: 15px;
  font-weight: 500;
  position: relative;
  overflow: hidden;
}

.sidebar-nav .nav-link:hover {
  background-color: rgba(102, 126, 234, 0.06);
  color: #2d3748;
  transform: translateX(2px);
}

.sidebar-nav .nav-link.active {
  background: linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.08) 100%);
  color: #4c51bf;
  font-weight: 600;
}

.sidebar-nav .nav-link.active::after {
  content: '';
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 3px;
  height: 20px;
  background: linear-gradient(to bottom, #667eea, #764ba2);
  border-radius: 3px 0 0 3px;
}

.sidebar-nav .nav-icon {
  margin-right: 14px;
  width: 20px;
  text-align: center;
  font-size: 18px;
  transition: transform 0.2s ease;
  opacity: 0.8;
}

.sidebar-nav .nav-link.active .nav-icon {
  opacity: 1;
}

.sidebar-nav .nav-link:hover .nav-icon {
  transform: scale(1.05);
}

.sidebar-logo {
  font-family: 'Inter', sans-serif;
  font-weight: 700;
  font-size: 24px;
  text-align: center;
  margin-bottom: 40px;
  padding: 0 16px;
  color: #2d3748;
  text-decoration: none;
  letter-spacing: -0.5px;
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
}

.sidebar-logo span {
  font-size: 24px;
  margin-right: 0;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  display: inline-block;
}

.sidebar-logo::after {
  content: '';
  display: block;
  width: 40px;
  height: 3px;
  background: linear-gradient(90deg, #667eea, #764ba2);
  margin: 12px auto 0;
  border-radius: 2px;
}

.sidebar-close-btn {
  position: absolute;
  top: 20px;
  right: 20px;
  background: #f7fafc;
  border: 1px solid #e2e8f0;
  color: #4a5568;
  width: 36px;
  height: 36px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 18px;
  transition: all 0.2s ease;
  z-index: 101;
}

.sidebar-close-btn:hover {
  background: #edf2f7;
  transform: rotate(90deg);
  border-color: #cbd5e0;
}

.loading-state {
  padding: 20px 16px;
  text-align: center;
  color: #a0aec0;
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.loading-dots {
  display: inline-flex;
  gap: 4px;
}

.loading-dots span {
  width: 6px;
  height: 6px;
  background-color: #cbd5e0;
  border-radius: 50%;
  animation: pulse 1.5s ease-in-out infinite;
}

.loading-dots span:nth-child(2) {
  animation-delay: 0.2s;
}

.loading-dots span:nth-child(3) {
  animation-delay: 0.4s;
}

@keyframes pulse {
  0%, 100% {
    opacity: 0.4;
    transform: scale(0.8);
  }
  50% {
    opacity: 1;
    transform: scale(1);
  }
}

.sidebar-footer {
  margin-top: auto;
  padding: 20px 24px;
  border-top: 1px solid #e2e8f0;
  font-size: 12px;
  color: #a0aec0;
  text-align: center;
  letter-spacing: 0.3px;
}

`;

// CSS'i document head'e ekle
if (!document.getElementById('sidebar-styles')) {
  const styleSheet = document.createElement("style");
  styleSheet.id = 'sidebar-styles';
  styleSheet.innerText = styles;
  document.head.appendChild(styleSheet);
}

export default function Sidebar({ onClose, isMobile }) {
  const [userRole, setUserRole] = useState("student");
  const [loading, setLoading] = useState(true);
  const apiUrl = useContext(ApiUrlContext);
  const location = useLocation();

  useEffect(() => {
    const fetchUserRole = async () => {
      try {
        const token = localStorage.getItem("token");
        if (token) {
          const response = await fetch(`${apiUrl}/user/profile`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (response.ok) {
            const contentType = response.headers.get("content-type") || "";
            if (contentType.includes("application/json")) {
              const userData = await response.json();
              setUserRole(userData.role || "student");
            } else {
              console.warn("Beklenen JSON dönmedi:", await response.text());
              setUserRole("student");
            }
          } else {
            setUserRole("student");
          }
        }
      } catch (error) {
        console.error("Kullanıcı rol bilgisi alınamadı:", error);
        setUserRole("student");
      } finally {
        setLoading(false);
      }
    };
    
    fetchUserRole();
    
    const interval = setInterval(fetchUserRole, 30000);
    return () => clearInterval(interval);
  }, [apiUrl]);

  const filteredMenu = menu.filter(item => {
    if (item.adminOnly) {
      return userRole === "admin";
    }
    if (item.instructorOnly) {
      return userRole === "instructor" || userRole === "admin";
    }
    return true;
  });

  return (
    <>
<aside
  style={{
    display: "flex",
    flexDirection: "column",
    padding: "28px 0",
    height: "100vh",
    overflowY: "auto",
    // Masaüstünde 250px sabit; mobilde çekmece kapsayıcısının genişliğini alır (index.css .sidebar-drawer)
    width: isMobile ? "100%" : 250,
    flexShrink: 0,
    position: "relative"
  }}
  className="sidebar"
>
        {/* Mobilde kapatma butonu */}
        {onClose && (
          <button
            onClick={onClose}
            className="sidebar-close-btn"
            aria-label="Menüyü kapat"
          >
            <X />
          </button>
        )}
        <div style={{ padding: "0 16px" }}>
        <a
          className="sidebar-logo"
          href="https://ihh.org.tr/"
          target="_blank"
          rel="noopener noreferrer"
          aria-label="İyiliğin Mucitleri ana sayfasını aç"
        >
          <span role="img" aria-label="logo" >
            ⏻
          </span>
          İyiliğin Mucitleri
        </a>
        </div>
        <nav className="sidebar-nav" style={{ flex: 1 }}>
          {loading ? (
            <div className="loading-state">
              <span>Yükleniyor</span>
              <div className="loading-dots">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
          ) : (
            filteredMenu.map((item) => (
              <NavLink
                key={item.label}
                to={item.path}
                className={({ isActive }) => 
                  `nav-link ${isActive ? "active" : ""}`
                }
                end
                onClick={isMobile ? onClose : undefined}
              >
                <span className="nav-icon" aria-hidden="true">
                  {item.icon}
                </span>
                {item.label}
              </NavLink>
            ))
          )}
        </nav>
        
        <div className="sidebar-footer">
          <div style={{ opacity: 0.8, marginBottom: 4 }}>
            İHH Eğitim Platformu
          </div>
          <div style={{ fontSize: 11 }}>
            v2.1 • {new Date().getFullYear()}
          </div>
        </div>
      </aside>
    </>
  );
}
